import { type InputHTMLAttributes, forwardRef } from "react";

interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  label: string;
  value: number;
  onValueChange: (n: number) => void;
  helper?: string;
  suffix?: string;
  required?: boolean;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ label, value, onValueChange, helper, suffix = "€", required, id, ...rest }, ref) => {
    const inputId = id ?? `n-${label.replace(/\s+/g, "-").toLowerCase()}`;
    return (
      <div className="space-y-1.5">
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="text"
            inputMode="decimal"
            value={value === 0 ? "" : value.toString()}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
              const n = parseFloat(cleaned);
              onValueChange(Number.isFinite(n) ? n : 0);
            }}
            className="block w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-10 text-base text-foreground tabular shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
            {...rest}
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
            {suffix}
          </span>
        </div>
        {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
      </div>
    );
  }
);
NumberInput.displayName = "NumberInput";
